import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { Language } from '../../shared/domain/Language'
import type { LoginStatus } from '../../auth/domain/LoginStatus'
import type { Affiliate } from '../../shared/domain/Affiliate'
import { Market } from '../../shared/domain/Market'
import type { Devices } from '../../shared/domain/Device'
import type { GETBannerInformationResponseType } from '../infrastructure/HTTPMarketingRepository/schemas/banners/banners-json'
import { Banner } from './Banner'
import type { BannerSubtypes } from './BannerSubtype'
import type { BannerTypes } from './BannerType'
import type { GETBannerInformationResponseSafeType } from '../infrastructure/HTTPMarketingRepository/schemas/banners'

export const ListOfBannerValidation = z.object({
  list: z.instanceof(Banner, { message: '[ListOfBanner] list is required' }).array(),
  market: z.instanceof(Market, { message: '[ListOfBanner] market is required' }),
  language: z.instanceof(Language, { message: '[ListOfBanner] language is required' }),
})

export class ListOfBanner extends Model {
  static create({ list, market, language }: z.infer<typeof ListOfBannerValidation>) {
    ListOfBannerValidation.parse({ list, market, language })
    return new ListOfBanner(list, market, language)
  }

  static empty() {
    return new ListOfBanner([], Market.empty(), Language.empty())
  }

  static fromDatasetOrEmpty({
    list,
    market,
    language,
  }: {
    list: GETBannerInformationResponseType['banners']
    market: Market
    language: Language
  }) {
    try {
      return ListOfBanner.fromLegacyApi({
        list,
        market,
        language,
      })
    } catch {
      return ListOfBanner.empty()
    }
  }

  static fromCurrentApi({
    list,
    market,
    language,
  }: {
    list: GETBannerInformationResponseSafeType
    market: Market
    language: Language
  }) {
    const validBanners = list
      .map(item => {
        try {
          return Banner.fromCurrentApiDataset({ info: item, market, language })
        } catch {
          return null
        }
      })
      .filter((banner): banner is Banner => banner !== null)

    return ListOfBanner.create({
      list: validBanners,
      market,
      language,
    })
  }

  static fromLegacyApi({
    list,
    market,
    language,
  }: {
    list: GETBannerInformationResponseType['banners']
    market: Market
    language: Language
  }) {
    // Filter out invalid banners by attempting to create each one individually
    const validBanners = list
      .map(item => {
        try {
          return Banner.fromDataset({ info: item, market, language })
        } catch {
          // Skip invalid banners with hero banner exception
          if (item.type === 'hero-banner') {
            return Banner.createEmptyHeroBanner(market, language)
          }
          return null
        }
      })
      .filter((banner): banner is Banner => banner !== null)

    return ListOfBanner.create({
      list: validBanners,
      market,
      language,
    })
  }

  static fromJSON(json: ReturnType<ListOfBanner['toJSON']>) {
    return new ListOfBanner(
      json.list.map(item =>
        Banner.fromJSON(item, Market.fromJSON(json.market), Language.fromJSON(json.language))
      ),
      Market.fromJSON(json.market),
      Language.fromJSON(json.language)
    )
  }

  static fromJSONOrEmpty(json: ReturnType<ListOfBanner['toJSON']>) {
    try {
      return ListOfBanner.fromJSON(json)
    } catch {
      return ListOfBanner.empty()
    }
  }

  constructor(
    private readonly _list: Banner[],
    private readonly _market: Market,
    private readonly _language: Language
  ) {
    super()
  }

  get list() { return this._list } // prettier-ignore

  get market() { return this._market } // prettier-ignore
  get language() { return this._language } // prettier-ignore

  getBannerByType(type: BannerTypes) {
    return this._list.find(bannerInfo => bannerInfo.getType() === type)
  }

  getBannerBySubtype(subtype: BannerSubtypes) {
    return this._list.find(bannerInfo => bannerInfo.getSubtype() === subtype)
  }

  getBannerBySubtypeAndViewport(subtype: BannerSubtypes, viewport: Devices) {
    const specificBanner = this._list.find(
      bannerInfo => bannerInfo.getSubtype() === subtype && bannerInfo.viewport?.is(viewport)
    )

    if (specificBanner) {
      return specificBanner
    }

    return this._list.find(
      bannerInfo => bannerInfo.getSubtype() === subtype && !bannerInfo.viewport
    )
  }

  getBillboardBannerInformation({
    market,
    affiliate,
    loginStatus,
    device,
  }: {
    market?: Market
    affiliate?: Affiliate
    loginStatus?: LoginStatus
    device: Devices
  }): ReturnType<Banner['getBillboardBannerInformation']> {
    const campaignBanner = this.getBannerBySubtypeAndViewport('campaign', device)

    if (campaignBanner) {
      return campaignBanner.getBillboardBannerInformation()
    }

    if (
      device === 'mobile' &&
      !market?.isOneOf(['es', 'us']) &&
      loginStatus?.isUnlogged() &&
      affiliate?.isCug()
    ) {
      const memberBanner = this.getBannerBySubtypeAndViewport('member', device)

      return memberBanner?.getBillboardBannerInformation()
    }

    return this.getBannerBySubtypeAndViewport('brand', device)?.getBillboardBannerInformation()
  }

  getAnnouncementBannerInformation({
    device,
  }: {
    device: Devices
  }): ReturnType<Banner['getAnnouncementBannerInformation']> {
    const banner = this._list.find(
      bannerInfo =>
        bannerInfo.getType() === 'announcement-banner' &&
        (bannerInfo.viewport?.is(device) || !bannerInfo.viewport)
    )

    return banner?.getAnnouncementBannerInformation()
  }

  getBlockBannerInformation({
    device,
  }: {
    device: Devices
  }): ReturnType<Banner['getBlockBannerInformation']> {
    const banner = this._list.find(
      bannerInfo =>
        bannerInfo.getType() === 'block-banner' &&
        (bannerInfo.viewport?.is(device) || !bannerInfo.viewport)
    )

    return banner?.getBlockBannerInformation()
  }

  getHeroBannerInformation({
    device,
  }: {
    device: Devices
  }): ReturnType<Banner['getHeroBannerInformation']> {
    const banner = this._list.find(
      bannerInfo =>
        bannerInfo.getType() === 'hero-banner' &&
        (bannerInfo.viewport?.is(device) || !bannerInfo.viewport)
    )

    return banner?.getHeroBannerInformation()
  }

  toJSON() {
    return {
      list: this._list.map(item => item.toJSON()),
      market: this._market.toJSON(),
      language: this._language.toJSON(),
    }
  }
}
